import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useMainStore } from './store.js'

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * CDN 域名替换 composable
 * 1. 保持兼容原有 siteInfo.configData.cdnDomainMap 的纯域名映射能力
 * 2. 当 configData.isOpenCloudFlareImage 为真时，额外把老 front-site 图片地址改成 Cloudflare 地址
 */
export function useCdnDomain() {
  const store = useMainStore()
  const { siteInfo } = storeToRefs(store)

  const getParsedConfigData = () => {
    if (!siteInfo.value || !siteInfo.value.configData) {
      return null
    }

    let configData = siteInfo.value.configData

    if (typeof configData === 'string') {
      try {
        configData = JSON.parse(configData)
      } catch (e) {
        console.error('Failed to parse configData:', e)
        return null
      }
    }

    return configData
  }

  const getCdnDomainMap = () => {
    return getParsedConfigData()?.cdnDomainMap || {}
  }

  const cdnDomainMap = computed(() => {
    return getCdnDomainMap()
  })

  const replaceCloudflareImageUrl = (url, configData) => {
    if (!configData?.isOpenCloudFlareImage || !url || typeof url !== 'string') {
      return url
    }

    if (/^https?:\/\/front-site\.oss-accelerate\.aliyuncs\.com/i.test(url)) {
      return url.replace(/^https?:\/\/front-site\.oss-accelerate\.aliyuncs\.com/i, 'https://rsomszt.com/oss-img')
    }

    if (/^https?:\/\/sztjyshop\.oss-cn-guangzhou\.aliyuncs\.com/i.test(url)) {
      return url.replace(/^https?:\/\/sztjyshop\.oss-cn-guangzhou\.aliyuncs\.com/i, 'https://rsomszt.com/oss-sztj')
    }

    if (
      !/^https?:\/\/front-site\.oss-accelerate\.aliyuncs\.com/i.test(url)
      && !/^https?:\/\/sztjyshop\.oss-cn-guangzhou\.aliyuncs\.com/i.test(url)
    ) {
      return url
    }
    return url
  }

  const replaceCdnDomain = (url) => {
    if (!url || typeof url !== 'string') {
      return url
    }

    const configData = getParsedConfigData()
    const cloudflareReplacedUrl = replaceCloudflareImageUrl(url, configData)
    const domainMap = configData?.cdnDomainMap || {}

    if (!domainMap || Object.keys(domainMap).length === 0) {
      return cloudflareReplacedUrl
    }

    try {
      const urlObj = new URL(cloudflareReplacedUrl)
      const originalHost = urlObj.host

      for (const [key, value] of Object.entries(domainMap)) {
        if (originalHost.includes(key)) {
          urlObj.host = value
          return urlObj.toString()
        }
      }

      return cloudflareReplacedUrl
    } catch (e) {
      let replacedUrl = cloudflareReplacedUrl
      for (const [key, value] of Object.entries(domainMap)) {
        if (replacedUrl.includes(key)) {
          const regex = new RegExp(`(https?://)${escapeRegExp(key)}`, 'gi')
          replacedUrl = replacedUrl.replace(regex, `$1${value}`)
        }
      }
      return replacedUrl
    }
  }

  return {
    cdnDomainMap,
    getCdnDomainMap,
    replaceCdnDomain,
  }
}
